import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";

const Footer = () => {
  const socialLinks = [
    { href: "https://www.facebook.com/profile.php?id=100086638418720", icon: Facebook, label: "Facebook", color: "hover:text-blue-500" },
    { href: "https://www.instagram.com/marcohvacsurat/", icon: Instagram, label: "Instagram", color: "hover:text-pink-500" },
    { href: "https://www.linkedin.com/company/90358068/admin/dashboard/", icon: Linkedin, label: "LinkedIn", color: "hover:text-blue-400" },
  ];

  return (
    <footer className="bg-gradient-to-br from-white via-amber-50/50 to-white text-gray-700 border-t border-amber-200" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logo-marco.png" 
                alt="MARCO AIR CONDITION Logo"
                className="h-16 w-auto hover:scale-105 transition-transform duration-300"
              />
              <span className="text-lg font-extrabold text-gray-900 tracking-wide leading-tight">MARCO <span className="text-amber-600">AIR CONDITION</span></span>
            </div>
            <p className="text-gray-600 mb-4">
              Your trusted partner for all HVAC needs. Quality service, professional team, and customer satisfaction guaranteed.
            </p>
            <p className="text-amber-600 italic font-semibold mb-6">&ldquo;Experience Comfort Cooling&rdquo;</p>
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-gray-600 ${social.color} transition-all duration-300 hover:scale-125 p-2 bg-amber-100 rounded-full hover:bg-amber-200`}
                  aria-label={social.label}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-amber-600">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'Services', 'About Us', 'Contact'].map((link) => (
                <li key={link}>
                  <a 
                    href={`#${link.toLowerCase().replace(' ', '-')}`} 
                    className="text-gray-600 hover:text-amber-600 transition-all duration-300 hover:pl-2 inline-block"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-amber-600">Our Services</h4>
            <ul className="space-y-3">
              {['Air Chiller', 'VRF Systems', 'Ductable AC', 'Cassette AC', 'Split AC', 'AMC'].map((service) => (
                <li key={service}>
                  <a 
                    href="#services" 
                    className="text-gray-600 hover:text-amber-600 transition-all duration-300 hover:pl-2 inline-block"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-amber-600">Contact Info</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 group">
                <Phone className="h-5 w-5 text-amber-600 group-hover:animate-pulse" />
                <a href="tel:9825884460" className="text-gray-600 hover:text-amber-600 transition-colors">9825884460</a>
              </li>
              <li className="flex items-center gap-3 group">
                <svg className="h-5 w-5 text-green-500 group-hover:animate-bounce" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <a 
                  href={getWhatsAppLink("Hi! I'm interested in your HVAC services.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-green-500 transition-colors"
                >
                  WhatsApp Us
                </a>
              </li>
              <li className="flex items-center gap-3 group">
                <Mail className="h-5 w-5 text-amber-600 group-hover:animate-pulse" />
                <a href="mailto:marcohvacsurat@gmail.com" className="text-gray-600 hover:text-amber-600 text-sm transition-colors">marcohvacsurat@gmail.com</a>
              </li>
              <li className="flex items-center gap-3 group">
                <Mail className="h-5 w-5 text-amber-600 group-hover:animate-pulse" />
                <a href="mailto:customercare@marcoaircondition.com" className="text-gray-600 hover:text-amber-600 text-sm transition-colors break-all">customercare@marcoaircondition.com</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <span className="text-gray-600 text-sm">Nanpura, Surat - 395001</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-amber-200 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600">
              © {new Date().getFullYear()} MARCO AIR CONDITION. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-gray-500 ${social.color} transition-all duration-300 hover:scale-125`}
                  aria-label={social.label}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
